import { ensureDateObject, convertTimeFraction, getTimeFraction, isNullOrEmpty } from '@/services/util.service.js'
import moment from "moment"
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";


const dayMap = [ 'SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY' ]

const createDate = (dt, time, dateOffset) => {
  const { hours, minutes } = convertTimeFraction(time)
  return moment(dt)
    .set("date", dt.getDate() + dateOffset)
    .set("hours", hours)
    .set("minutes", minutes)
    .set("seconds", 0)
    .toDate()
}
const normalizeDate = (date) => {
  return new Date(date.setMilliseconds(0)); 
};
export const filterAvailableSchedules = (schedules = [], dt = new Date(), advance = 0, onlyWithinSchedule=false) => {
  dt = ensureDateObject(dt)
  const todayMoment = moment(dt).day()

  // We want to use the advance hours to find a schedule that allows ordering
  const advanceDate = new Date(dt.toString())
  advanceDate.setHours(dt.getHours() + advance)

  let _availableSchedules = []

  // A menu can have multiple scheduled hours, so we want to find the widest range it's available
  for (const schedule of schedules) {
    if (schedule.type == 'DATE_TIME_RANGE') {
      // type: DATE_TIME_RANGE Is just a single date time range, inclusive -> exclusive

      // Look for any schedules that allow the advance time but have not already ended
      // Some schedules, like menu, allow for ordering in advance
      if (!onlyWithinSchedule || (advanceDate >= ensureDateObject(schedule.startDateTimeStr) && dt < ensureDateObject(schedule.endDateTimeStr))) {
        // nextAvailableDay is only used if the schedule is not for today
        _availableSchedules.push({
          start: schedule.startDateTimeStr,
          end: schedule.endDateTimeStr,
          nextAvailableDay: null,
          dayIndex: null,
          value: true
        })
      }
    } else {
      // Skip schedules that don't have the required data
      if (!schedule.days.length || !schedule.timeRange.length)
        continue

      // Look for the first day the schedule is available from today
      for (let offset = 0; offset < 7; offset++) {
        let dayIndex = todayMoment + offset

        // Because we start from today, not go through each day in order, the index will exceed 7
        dayIndex = dayIndex < 7 ? dayIndex : dayIndex-7
        const dayString = dayMap[dayIndex]

        // Skip days that weren't included in the list of schedule days
        if (!schedule.days.includes(dayString))
          continue

        for (const itemTimeRange of schedule.timeRange) {
          let { start, end, value } = itemTimeRange

          // Look for any schedules that allow the advance time but have not already ended
          // Some schedules, like menu, allow for ordering in advance
          if (!onlyWithinSchedule || (normalizeDate(advanceDate) >= normalizeDate(createDate(dt, start, offset)) && dt <= createDate(dt, end, offset))) {
            // nextAvailableDay is only used if the schedule is not for today
            _availableSchedules.push({
              start: start,
              end: end,
              nextAvailableDay: offset > 0 ? dayString : null,
              dayIndex: offset,
              value: value
            })
          }
        }
      }
    }
  }

  return _availableSchedules
}

export const sortSchedules = (schedules, dt, maintainOrder=false) => {
  if (!isNullOrEmpty(schedules))
    return schedules.sort((a,b) => {
      let aCost = 0, bCost = 0;

      // Usually we want the true next available order,
      // but sometimes we want the order that makes the most sense in order
      if (!maintainOrder) {
        // If the schdule is for today but the time is over, it's actually a full week away
        if (a.dayIndex === 0 && dt >= createDate(dt, a.end, 0))
        aCost -= 10

        // If the schdule is for today but the time is over, it's actually a full week away
        if (b.dayIndex === 0 && dt >= createDate(dt, b.end, 0))
        bCost -= 10
      }

      if (a.dayIndex > b.dayIndex) {
        bCost += 5
      } else if (a.dayIndex != b.dayIndex) {
        aCost += 5
      }

      if (a.start > b.start) {
        bCost += 2
      } else if (a.start != b.start) {
        aCost += 2
      }

      if (a.end > b.end) {
        bCost += 1
      } else if (a.end != b.end) {
        aCost += 1
      }

      return bCost > aCost ? 1 : -1
    })
  return schedules
}

export const scanSchedule = (schedules = [], dt = new Date(), advance = 0, onlyWithinSchedule=false) => {
  const _availableSchedules = sortSchedules(filterAvailableSchedules(schedules, dt, advance, onlyWithinSchedule), dt)

  if (!isNullOrEmpty(_availableSchedules))
    return _availableSchedules[0]
  return { start: null, end: null, nextAvailableDay: null, dayIndex: null, value: null }
}

export const allNextAvailableDaySchedules = (schedules = [], dt = new Date(), advance = 0, onlyWithinSchedule=false) => {
  const _availableSchedules = sortSchedules(filterAvailableSchedules(schedules, dt, advance, onlyWithinSchedule), dt, true)

  if (!isNullOrEmpty(_availableSchedules))
    return _availableSchedules.filter(_schedule => _schedule.dayIndex == _availableSchedules[0].dayIndex)
  return [ ]
}

export const isInSchedule = (schedules = [], dt = new Date(), advance = 0) => {
  if (!schedules || !schedules.length)
    return true
  
  return !isNullOrEmpty(sortSchedules(filterAvailableSchedules(schedules, ensureDateObject(dt), advance, true), dt))
}

export const findIntersectionHours = (a, b) => {
  return {
    start: a.start > b.start ? a.start : b.start,
    end: a.end < b.end ? a.end : b.end
  }
}

export const createSchedule = (day = null) => {
  return {
    days: [day].filter(Boolean),
    timeRange: [],
    type: "DAYS_OF_WEEK"
  }
}

export const createTimeRange = (a, b) => {
  return {
    start: a,
    end: b
  }
}

// The item schedule in effect on dt's date. An item's specialDaySchedule (BC2 "Special Days",
// e.g. a public holiday) replaces its weekly schedule on any date its DATE_TIME_RANGE items
// overlap for a positive length -- the item is then sold only inside those ranges. Same rule
// as Framework's Schedule.rangesOn / RTCatalogItem. The day's ranges come back as one
// DAYS_OF_WEEK item so they intersect with the store hours like any other schedule.
// ranges are store-local wall-clock strings (e.g. 2026-12-25T11:00:00), parsed the same way as dt.
export const getItemScheduleItems = (item, dt = new Date()) => {
  const weekly = get(item, 'schedule.items', []) || []
  const special = get(item, 'specialDaySchedule.items', []) || []
  if (!special.length) return weekly

  const dayStart = moment(ensureDateObject(dt)).startOf('day')
  const dayEnd = moment(dayStart).add(1, 'day')
  const ranges = []
  for (const range of special) {
    if (range.type !== 'DATE_TIME_RANGE' || !range.startDateTimeStr) continue
    const start = moment.max(moment(range.startDateTimeStr), dayStart)
    const end = range.endDateTimeStr ? moment.min(moment(range.endDateTimeStr), dayEnd) : dayEnd
    if (start.isBefore(end)) {
      ranges.push(createTimeRange(start.diff(dayStart, 'minutes') / 60, end.diff(dayStart, 'minutes') / 60))
    }
  }
  if (!ranges.length) return weekly

  return [{ ...createSchedule(dayMap[dayStart.day()]), timeRange: ranges }]
}

export const getTimeRangeForDay = (schedule, dow) => {
  for (const item of schedule) {
    if (item.days != null && item.days.includes(dow))
      return item.timeRange;
  }
  return null;
}

export const validateRanges = (timeRanges) => {
  return timeRanges.filter(timeRange => {
    return timeRange.start < timeRange.end
  })
}

export const mergeRange = (r1, r2) => {
  if(r1 == null && r2 == null)
    return null;
  if(r1 == null || r2 == null)
    return r1 || r2

  let result = []
  let i = 0, j = 0;

  while (i < r1.length && j < r2.length) {
    const s1 = r1[i].start;
    const e1 = r1[i].end;
    const s2 = r2[j].start;
    const e2 = r2[j].end;

    if(e1 < e2) {
      i++;
    } else {
      j++;
    }

    if (e1 >= s2 && e2 >= s1) {
      const endpoints = [s1, e1, s2, e2]

      endpoints.sort((a,b) => a-b);
      result.push(createTimeRange(endpoints[1], endpoints[2]));
    }
  }

  //compact the results
  let realResult = []

  if (result.length > 1) {
    for (let k=0; k < result.length-1; k++) {
      let range = result[k]
      let nextRange = result[k+1]

      if (range.start == range.end)
        //discard any range with same start and end
        continue;

      if (range.end == nextRange.start) {
        //merge the two ranges and skip the next one
        realResult.push(createTimeRange(range.start, nextRange.end));
        k++;
      } else {
        realResult.push(range);
      }
    }

    let lastRange = result[result.length-1]

    if (lastRange.start != lastRange.end) {
      realResult.push(lastRange);
    }

    return validateRanges(realResult);
  } else {
    return validateRanges(result);
  }
}

export const splitSchedule = (schedule) => {
  //Merge this schedule with another schedule to produce a new schedule, currently only doable for schedules of the same type
  let result = []

  for (const dow of dayMap) {
    //The new schedule will have 1 item representing each day
    const item = createSchedule();

    item.days.push(dow);
    if (schedule.days.includes(dow))
      item.timeRange = schedule.timeRange

    result.push(item)
  }

  return result;
}

export const mergeSchedules = (schedule, newSchedule) => {
  //Merge this schedule with another schedule to produce a new schedule, currently only doable for schedules of the same type
  let result = []

  for (const dow of dayMap) {
    //The new schedule will have 1 item representing each day
    const item = createSchedule();

    item.days.push(dow);

    const range1 = getTimeRangeForDay(schedule, dow);
    const range2 = getTimeRangeForDay(newSchedule, dow);

    if (range1 == null || range2 == null) {
      //no valid time range for this day
      continue
    } else {
      item.timeRange = mergeRange(range1, range2)
    }

    result.push(item);
  }

  return result;
}

export const combineCommonSchedules = (schedules) => {
  if (!(schedules && schedules.length))
    return false

  const compareTimes = (a,b) => {
    if ('timeRange' in a && 'timeRange' in b) {
      const rangeA = a.timeRange
      const rangeB = b.timeRange

      // Must be equal length to match
      if (rangeA.length == rangeB.length) {
        // Make sure every time range in the list exists in both
        for (const a of rangeA) {
          // Perform a search to allow time ranges being in different orders but containing the same values
          const found = !!rangeB.find(b => {
            return b.start == a.start && b.end == a.end
          })

          if (!found)
            return false
        }

        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  // Combine the list of individual days that have the same hours
  return schedules.map((schedule, index) => {
    // We don't care about schedules (days) that have no available times
    if (!schedule.timeRange.length)
      return null

    const firstOccurance = schedules.findIndex(s => compareTimes(schedule, s))

    if (firstOccurance == index) {
      // Is firstOccurance, no further action required
      return schedule
    } else if (firstOccurance >= 0) {
      // Is repeated, add the day(s) to the first
      schedules[firstOccurance].days = [
        ...schedules[firstOccurance].days,
        ...schedule.days
      ]

      // return null to be filtered out
      return null
    } else {
      // Emergancy fallback. Shouldn't be possible to not be found but just incase
      return null
    }
  }).filter(Boolean) // Filter out the repeats (which would now be nulls)
}

export const initSchedule = () => {
  return [...dayMap].map(dow => createSchedule(dow))
}

export const findIntersection = (scheduleObjects = []) => {
  let scheduleList = null
  let advance = null

  for (const obj of scheduleObjects) {
    // Schedules are required, advance is not
    if (!(obj.schedule && obj.schedule.length))
      continue

    // Use the shortest advance hours
    if (advance === null || obj.advance < advance)
      advance = obj.advance

    let newSchedule = initSchedule()

    for (let schedule of obj.schedule) {
      // Split each schedule into a list of days with their hours
      const newSplitSchedule = splitSchedule(schedule)

      // Combine the schedule object into one big list of available days
      for (let day of newSplitSchedule) {
        // Only add the days with data
        if (day.timeRange && day.timeRange.length) {
          // Don't depend on days being in same order (should be but don't depend on that)
          let index = newSchedule.findIndex(d => d.days[0] == day.days[0])

          // Only push if index could be found
          if (index >= 0) {
            newSchedule[index].timeRange = [
              ...newSchedule[index].timeRange,
              ...day.timeRange
            ]
          }
        }
      }
    }

    if (scheduleList === null) {
      // Initialize the scheduleList
      scheduleList = cloneDeep(newSchedule)
    } else {
      // Combine the new schedule with previous ones
      scheduleList = mergeSchedules(scheduleList, newSchedule)
    }
  }

  return {
    advance,
    schedule: combineCommonSchedules(scheduleList)
  }
}

export const compareSchedules = (scheduleObjects = [], dt = new Date()) => {
  const { advance, schedule } = findIntersection(scheduleObjects)

  return advance === null || (schedule && schedule.length && isInSchedule(schedule, dt, advance))
}